// A dropdown offers ONLY Rx sets containing that nutrient, via `setsFor` rather than a hand-written
// list, so it cannot drift from what the saved sets actually hold. CUSTOM ASKS FOR NUTRIENTS FIRST
// and offers only nutrients EVERY selected field has an Rx for.
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { X } from "lucide-react";
import { useOrgStore } from "../stores/orgStore";
import { useHydratedRecSets } from "../hooks/useHydratedRecSets";
import { basisToken, blendRates, type BlendBasis } from "../lib/spreadBlend";
import {
  buildSpreadZip,
  fileToken,
  productColumn,
  productLbsPerAc,
  type SpreadFieldExport,
} from "../lib/spreadExport";
import { buildSpreadPdf } from "../lib/spreadExport";
import {
  resolveSpreadRx,
  setsFor,
  SPREAD_RX_NUTRIENTS,
  sharedNutrients,
  type ResolvedSpreadRx,
  type SpreadRxNutrient,
} from "../lib/spreadRx";
import { JdOpsSend } from "./JdOpsSend";
import { SpreadRxTable } from "../features/spread-rx-table/SpreadRxTable";
import { SpreadNutrientChooser } from "../features/spread-nutrient/SpreadNutrientChooser";
import {
  SpreadEditorSide,
  SpreadProductRates,
} from "../features/spread-side/SpreadEditorSide";
import type { SpreadPreset } from "../lib/spreadPresets";
import type { DashboardField } from "../types/contract";
import { Modal } from "../ui/Modal";

interface RowState {
  setId: string;
  rx: ResolvedSpreadRx | null;
  loading: boolean;
  error: string | null;
  /* * the user chose this set in the dropdown — the hydration re-seed must never overwrite it */
  picked?: boolean;
}
const key = (fieldId: string, n: SpreadRxNutrient) => `${fieldId}|${n}`;

export function SpreadEditor({
  preset,
  fields,
  onClose,
}: {
  preset: SpreadPreset;
  fields: DashboardField[];
  onClose: () => void;
}) {
  const orgName = useOrgStore((s) => s.activeOrg()?.name ?? "");
  const fieldIds = fields.map((f) => f.field_id);
  // Every `setsFor` below is a per-ORIGIN localStorage read; without this the editor's dropdown
  // offers only the sets this browser happens to have cached and says "No saved <nutrient> Rx" for
  // the rest.
  const { hydrating, version } = useHydratedRecSets(fieldIds);

  // ---- which nutrients this run spends -----------------------------------------------------------
  const fieldKey = fieldIds.join(",");
  const available = useMemo(() => {
    // version bumps when saved Rx sets finish hydrating; the read is from localStorage.
    void version;
    return sharedNutrients(fieldKey ? fieldKey.split(",") : []);
  }, [fieldKey, version]);
  const fixed = preset.nutrient ? [preset.nutrient] : null; // a named product is not a choice
  const [picked, setPicked] = useState<SpreadRxNutrient[]>([]);
  const [chose, setChose] = useState(false);
  const nutrients = fixed ?? picked;
  const nutrientKey = nutrients.join(",");

  const [pct, setPct] = useState<Record<string, number>>(() =>
    Object.fromEntries(
      SPREAD_RX_NUTRIENTS.map((n) => [
        n,
        preset.nutrient === n ? preset.efficiencyPct : 100,
      ]),
    ),
  );
  // 'max' is default and the only basis that never under-applies: at the highest of the selected
  // nutrients' product rates, every other nutrient gets at least what it was recommended.
  const [basis, setBasis] = useState<BlendBasis>("max");
  const [rows, setRows] = useState<Record<string, RowState>>({});
  const [sendBusy, setSendBusy] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // Seed each (field, nutrient) with the NEWEST set — the card promises "automatically uses the most
  // recent Rx", so the editor has to open on that same choice or the two disagree on sight.
  useEffect(() => {
    const pickedNutrients = (
      nutrientKey ? nutrientKey.split(",") : []
    ) as SpreadRxNutrient[];
    setRows((prev) => {
      const next = { ...prev };
      for (const f of fields) {
        for (const n of pickedNutrients) {
          const k = key(f.field_id, n);
          const newest = setsFor(f.field_id, n)[0]?.id ?? "";
          if (!next[k]) {
            next[k] = { setId: newest, rx: null, loading: false, error: null };
            continue;
          }
          // The editor can open mid-hydration and seed from the pre-reconcile cache — an older set
          // or '' — and `if (next[k]) continue` then held that stale seed for the life of the panel
          // while the card beside it promised "the most recent Rx". Only rows the user has NOT
          // touched follow the newest set; an explicit dropdown pick (`picked`) always stands.
          if (!next[k].picked && newest && next[k].setId !== newest) {
            next[k] = { setId: newest, rx: null, loading: false, error: null };
          }
        }
      }
      return next;
    });
  }, [fields, nutrientKey, version]);

  // Resolve every chosen Rx. Keyed on the CHOSEN SETS so switching a dropdown re-resolves that pair
  // and nothing stale survives the switch.
  const chosen = fields
    .flatMap((f) =>
      nutrients.map(
        (n) => `${key(f.field_id, n)}=${rows[key(f.field_id, n)]?.setId ?? ""}`,
      ),
    )
    .join(",");
  const fieldsRef = useRef(fields);
  const nutrientsRef = useRef(nutrients);
  const rowsRef = useRef(rows);
  useLayoutEffect(() => {
    fieldsRef.current = fields;
    nutrientsRef.current = nutrients;
    rowsRef.current = rows;
  }, [fields, nutrients, rows]);
  useEffect(() => {
    const fields = fieldsRef.current;
    const nutrients = nutrientsRef.current;
    const rows = rowsRef.current;
    let alive = true;
    for (const f of fields) {
      for (const n of nutrients) {
        const k = key(f.field_id, n);
        const setId = rows[k]?.setId;
        const set = setsFor(f.field_id, n).find((s) => s.id === setId);
        if (!set || rows[k]?.rx?.set.id === setId) continue;
        setRows((r) => ({
          ...r,
          [k]: { ...r[k]!, loading: true, error: null },
        }));
        void resolveSpreadRx(f.field_id, n, set)
          .then((rx) => {
            if (alive)
              setRows((r) => ({ ...r, [k]: { ...r[k]!, rx, loading: false } }));
          })
          .catch((e: unknown) => {
            if (alive)
              setRows((r) => ({
                ...r,
                [k]: {
                  ...r[k]!,
                  rx: null,
                  loading: false,
                  error:
                    e instanceof Error ? e.message : "Couldn’t read that Rx.",
                },
              }));
          });
      }
    }
    return () => {
      alive = false;
    };
  }, [chosen]);

  // A basis naming a nutrient that has since been deselected would silently produce nothing — fall
  // back to the default rather than leaving the toolbar describing a rule that no longer applies.
  useEffect(() => {
    if (basis !== "max" && basis !== "avg" && !nutrients.includes(basis))
      setBasis("max");
  }, [basis, nutrients]);

  const column = preset.custom
    ? `${fileToken(preset.label)}${basisToken(basis)}`.slice(0, 10)
    : productColumn(preset.label, pct[preset.nutrient!] ?? 100);
  const pctOk = nutrients.every(
    (n) => Number.isFinite(pct[n]) && (pct[n] ?? 0) > 0,
  );

  /* * Every field with at least one resolved Rx, blended to a single product rate. */
  const exports = useMemo((): (SpreadFieldExport & {
    fieldId: string;
    avg: number | null;
  })[] => {
    const pickedNutrients = (
      nutrientKey ? nutrientKey.split(",") : []
    ) as SpreadRxNutrient[];
    const out: (SpreadFieldExport & { fieldId: string; avg: number | null })[] =
      [];
    for (const f of fields) {
      const resolved = pickedNutrients
        .map((n) => ({ n, rx: rows[key(f.field_id, n)]?.rx }))
        .filter((r): r is { n: SpreadRxNutrient; rx: ResolvedSpreadRx } =>
          Boolean(r.rx),
        );
      // ALL picked nutrients or NONE (pre-ship review, wrong-send class): a Custom Lime+K₂O blend
      // whose K₂O Rx errored or is still resolving used to export a lime-only rate set under the
      // full blend's column name — into the DOWNLOAD zip and the JD send alike.
      if (!resolved.length || resolved.length !== pickedNutrients.length)
        continue;
      const cells = Math.max(...resolved.map((r) => r.rx.geoms.length));
      const rates = blendRates(
        resolved.map((r) => ({
          nutrient: r.n,
          rates: r.rx.rates.map((v) =>
            productLbsPerAc(v, pct[r.n] ?? 100, r.n),
          ),
        })),
        basis,
        cells,
      );
      const present = rates.filter((v): v is number => v != null);
      if (!present.length) continue;
      out.push({
        fieldId: f.field_id,
        // a single-nutrient spend has ONE answering Rx set — that id rides the JD send history; a
        // Custom blend spends several, so no single rxId is the truth and none is claimed
        rxId:
          resolved.length === 1
            ? rows[key(f.field_id, resolved[0]!.n)]?.setId
            : undefined,
        fieldName: f.field_name,
        farmName: orgName,
        products: [{ column, rates }],
        geoms: resolved[0]!.rx.geoms,
        avg: present.reduce((a, v) => a + v, 0) / present.length,
      });
    }
    return out;
  }, [fields, rows, pct, basis, nutrientKey, column, orgName]);

  const canDownload = exports.length > 0 && pctOk;

  const save = (name: string, data: Uint8Array, type: string) => {
    const url = URL.createObjectURL(
      new Blob([data as unknown as BlobPart], { type }),
    );
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  };

  const download = async () => {
    if (!canDownload) return;
    setDownloadError(null);
    try {
      const { name, data } = await buildSpreadZip({
        fields: exports.map(
          ({ fieldId, fieldName, farmName, products, geoms }) => ({
            fieldId,
            fieldName,
            farmName,
            products,
            geoms,
          }),
        ),
        growerName: orgName,
        now: new Date(),
      });
      save(name, data, "application/zip");
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : "Download failed.");
    }
  };

  const downloadPdf = async () => {
    if (!canDownload) return;
    setDownloadError(null);
    try {
      const { name, data } = await buildSpreadPdf({
        fields: exports.map(
          ({ fieldId, fieldName, farmName, products, geoms }) => ({
            fieldId,
            fieldName,
            farmName,
            products,
            geoms,
          }),
        ),
        productLabel: preset.label,
        columnName: column,
        growerName: orgName,
        now: new Date(),
      });
      save(name, data, "application/pdf");
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : "Download failed.");
    }
  };

  const shell = (body: React.ReactNode, side?: React.ReactNode) => (
    <Modal>
      <div className="fixed inset-0 z-panel flex flex-col bg-surface text-fg sm:inset-4 sm:rounded-lg sm:shadow-2xl">
        <div className="flex items-center justify-between gap-2 border-b border-line px-3 py-2">
          <h2 className="truncate text-sm font-semibold">
            Spread {preset.label}{" "}
            <span className="font-normal text-muted">
              — {fields.length} field{fields.length === 1 ? "" : "s"}
            </span>
          </h2>
          <button
            className="shrink-0 rounded p-1 text-muted hover:bg-hover disabled:opacity-40"
            onClick={onClose}
            disabled={sendBusy}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
        <div className="flex min-h-0 flex-1 flex-col sm:flex-row">
          <div className="min-h-0 flex-1 overflow-auto p-3">{body}</div>
          {side}
        </div>
      </div>
    </Modal>
  );

  // ---- CUSTOM, step 1: which nutrients ------------------------------------------------------------
  if (preset.custom && !chose) {
    return shell(
      <SpreadNutrientChooser
        fieldCount={fields.length}
        available={available}
        picked={picked}
        onPicked={setPicked}
        onContinue={() => setChose(true)}
      />,
    );
  }

  // ---- the editor ---------------------------------------------------------------------------------
  const basisOptions: BlendBasis[] = ["max", "avg", ...nutrients];

  return shell(
    <div className="flex flex-col gap-4">
      <SpreadRxTable
        fields={fields}
        nutrients={nutrients}
        rows={rows}
        hydrating={hydrating}
        onPick={(fieldId, nutrient, setId) => {
          const k = key(fieldId, nutrient);
          setRows((r) => ({
            ...r,
            [k]: {
              setId,
              rx: null,
              loading: false,
              error: null,
              picked: true,
            },
          }));
        }}
      />

      {[
        ...new Set(
          Object.values(rows).flatMap((row) =>
            [row.rx?.notice, row.rx?.fileNotice].filter(
              (message): message is string => !!message,
            ),
          ),
        ),
      ].map((message) => (
        <p key={message} className="text-[11px] text-muted">
          {message}
        </p>
      ))}

      <SpreadProductRates exports={exports} />
    </div>,

    <SpreadEditorSide
      nutrients={nutrients}
      pct={pct}
      onPct={(nutrient, value) => setPct((p) => ({ ...p, [nutrient]: value }))}
      pctOk={pctOk}
      basis={basis}
      basisOptions={basisOptions}
      onBasis={setBasis}
      column={column}
      readyCount={exports.length}
      fieldCount={fields.length}
      downloadError={downloadError}
      canDownload={canDownload}
      onDownload={() => void download()}
      onDownloadPdf={() => void downloadPdf()}
      send={
        <JdOpsSend
          fields={fields}
          exports={exports}
          growerName={orgName}
          ourProduct={preset.label}
          disabled={!canDownload}
          onSendingChange={setSendBusy}
        />
      }
    />,
  );
}
